import { Button, ICON_SIZE_MAP, } from "../components/Button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger, } from "../components/Dropdown";
import { Icon } from "../components/Icon";
import { ChevronRight } from "../icons/v2-stroke";
import { cn } from "../lib";
import { cva } from "class-variance-authority";
import React from "react";
const DEFAULT_LABEL_TRUNCATE_LENGTH_MIDDLE = 15;
const DEFAULT_LABEL_TRUNCATE_LENGTH_END = 30;
const ELLIPSIS_STRING = "...";
const breadcrumbTextVariants = cva("", {
    variants: {
        isLast: {
            true: "text-foreground",
            false: "text-muted-foreground",
        },
        size: {
            xs: "",
            sm: "",
        },
        hasLighterFont: {
            true: "",
            false: "",
        },
    },
    compoundVariants: [
        { size: "xs", hasLighterFont: true, className: "text-xs" },
        { size: "sm", hasLighterFont: true, className: "text-sm" },
        { size: "xs", hasLighterFont: false, className: "label-xs" },
        { size: "sm", hasLighterFont: false, className: "label-sm" },
    ],
    defaultVariants: {
        size: "sm",
        hasLighterFont: true,
        isLast: false,
    },
});
const isLinkItem = (item) => "href" in item && typeof item.href === "string";
const isButtonItem = (item) => "onClick" in item && typeof item.onClick === "function";
function BreadcrumbItemRenderer({ item, isLast, itemsHidden, size = "sm", buttonVariant = "ghost", hasLighterFont = true, truncateLengthMiddle = DEFAULT_LABEL_TRUNCATE_LENGTH_MIDDLE, truncateLengthEnd = DEFAULT_LABEL_TRUNCATE_LENGTH_END, }) {
    if (item.label === ELLIPSIS_STRING) {
        return (React.createElement(DropdownMenu, null,
            React.createElement(DropdownMenuTrigger, { asChild: true },
                React.createElement(Button, { variant: buttonVariant, label: ELLIPSIS_STRING, icon: item.icon, size: size, hasLighterFont: hasLighterFont })),
            React.createElement(DropdownMenuContent, { align: "start" },
                React.createElement(DropdownMenuGroup, null, itemsHidden === null || itemsHidden === void 0 ? void 0 : itemsHidden.map((item, index) => (React.createElement(DropdownMenuItem, { key: `breadcrumbs-hidden-${index}`, href: isLinkItem(item) ? item.href : undefined, onClick: isButtonItem(item) ? item.onClick : undefined, icon: item.icon, label: item.label })))))));
    }
    const textClassName = breadcrumbTextVariants({
        isLast,
        size,
        hasLighterFont,
    });
    const truncatedLabel = truncateTextToLength(item.label, isLast ? truncateLengthEnd : truncateLengthMiddle);
    const isLabelTruncated = truncatedLabel !== item.label;
    if (isLinkItem(item)) {
        return (React.createElement(Button, { href: item.href, icon: item.icon, variant: buttonVariant !== null && buttonVariant !== void 0 ? buttonVariant : (isLast ? "ghost" : "ghost-secondary"), label: truncatedLabel, tooltip: isLabelTruncated ? item.label : undefined, size: size, hasLighterFont: hasLighterFont }));
    }
    if (isButtonItem(item)) {
        return (React.createElement(Button, { onClick: item.onClick, icon: item.icon, variant: buttonVariant !== null && buttonVariant !== void 0 ? buttonVariant : (isLast ? "ghost" : "ghost-secondary"), label: truncatedLabel, tooltip: isLabelTruncated ? item.label : undefined, size: size, hasLighterFont: hasLighterFont }));
    }
    if (item.icon) {
        return (React.createElement("div", { className: "shrink0 label-sm inline-flex h-9 items-center gap-2 border border-transparent px-3" },
            React.createElement(Icon, { visual: item.icon, size: ICON_SIZE_MAP[size], className: cn("-mx-0.5") }),
            React.createElement("div", { className: textClassName }, item.label)));
    }
    return React.createElement("div", { className: cn("px-2 py-1.5", textClassName) }, item.label);
}
/**
 * Displays the user's location within a hierarchy as a trail of clickable segments,
 * driven by an `items` array. Long trails automatically collapse middle segments
 * into an ellipsis menu and truncate overflowing labels. Use it to show and navigate
 * the path to the current page; for switching between sibling views rather than
 * levels of depth, use Tabs instead.
 * @summary Hierarchical navigation trail.
 */
export function Breadcrumbs({ items, className, size = "sm", buttonVariant = "ghost", hasLighterFont = true, truncateLengthMiddle, truncateLengthEnd, }) {
    const { itemsShown, itemsHidden } = items.reduce((acc, item, index) => {
        if (items.length <= 5 || index < 2 || index >= items.length - 2) {
            acc.itemsShown.push(item);
        }
        else if (index === 2) {
            acc.itemsShown.push({ label: ELLIPSIS_STRING });
            acc.itemsHidden.push(item);
        }
        else {
            acc.itemsHidden.push(item);
        }
        return acc;
    }, { itemsShown: [], itemsHidden: [] });
    return (React.createElement("div", { className: cn("flex flex-row items-center gap-0", className) }, itemsShown.map((item, index) => {
        return (React.createElement("div", { key: `breadcrumbs-${index}`, className: "flex flex-row items-center gap-0" },
            React.createElement(BreadcrumbItemRenderer, { item: item, isLast: index === itemsShown.length - 1, itemsHidden: itemsHidden, size: size, buttonVariant: buttonVariant, hasLighterFont: hasLighterFont, truncateLengthMiddle: truncateLengthMiddle, truncateLengthEnd: truncateLengthEnd }),
            index === itemsShown.length - 1 ? null : (React.createElement(Icon, { visual: ChevronRight, className: "text-faint", size: size === "xs" ? "xs" : "sm" }))));
    })));
}
function truncateTextToLength(text, length) {
    return text.length > length
        ? `${text.substring(0, length - 1)}${ELLIPSIS_STRING}`
        : text;
}
/** Composable breadcrumb root: a nav landmark wrapping BreadcrumbItem children. */
export function Breadcrumb({ children, className }) {
    return (React.createElement("nav", { "aria-label": "Breadcrumb", className: cn("flex flex-row items-center gap-0", className) }, children));
}
/** Composable wrapper for one breadcrumb segment inside a Breadcrumb. */
export function BreadcrumbItem({ children, className }) {
    return (React.createElement("div", { className: cn("flex flex-row items-center", className) }, children));
}
/** Clickable breadcrumb segment rendered as a Button. */
export function BreadcrumbButton({ label, onClick, variant = "ghost", size = "sm", icon, }) {
    return (React.createElement(Button, { label: label, onClick: onClick, variant: variant, size: size, icon: icon, hasLighterFont: true }));
}
/** Non-interactive current-page segment (`aria-current="page"`). */
export function BreadcrumbPage({ children, className }) {
    return (React.createElement("span", { "aria-current": "page", className: cn("inline-flex h-9 items-center px-3", breadcrumbTextVariants({
            isLast: true,
            size: "sm",
            hasLighterFont: true,
        }), className) }, children));
}
/** Chevron separator between breadcrumb segments. */
export function BreadcrumbSeparator({ className }) {
    return (React.createElement(Icon, { "aria-hidden": "true", visual: ChevronRight, className: cn("text-faint", className), size: "sm" }));
}
//# sourceMappingURL=Breadcrumbs.js.map